/**
 * Settings → AI (slice 1c spec §6.1): the Models card, the Providers card
 * with a row per connected provider, and Add a provider's window. Every
 * change is a `ConnectAI` on the computer, one at a time: while one runs
 * (`connecting`) the controls wait, and `/api/me` follows it to where it
 * settles. The connect panel (S1b's) opens where it was asked for: in a
 * provider's row (Add another account, Replace, Reconnect) or in Add a
 * provider's window, so a sign-in's code (ChatGPT's or Grok's) shows where
 * it was started. What each piece shows comes from `ai-view.ts`.
 */
import { Button } from "@crew/ui/components/button";
import { StatusDot } from "@crew/ui/components/status";
import { cn } from "@crew/ui/lib/utils";
import { useCallback, useEffect, useState } from "react";
import { useNow } from "../../app/now";
import type { AiProvider, Me } from "../../app/me";
import { useConnectAi, useModels, useReadHealth } from "../setup/api";
import { ConnectPanel, type Connect } from "../setup/connect-panel";
import { connectPicksDefault, PROVIDERS } from "../setup/onboarding";
import { AddProviderWindow } from "./add-provider";
import { Show } from "./ai-parts";
import {
  aiNotice,
  defaultCard,
  handsOff,
  hasCredential,
  makerRows,
  providerCards,
  slotPlaces,
} from "./ai-view";
import { ModelsCard } from "./models-card";
import { ProvidersCard, type Open } from "./provider-rows";
import { LeavePrompt, SaveBar } from "./routing-save";
import { useRoutingDraft } from "./use-routing-draft";

type Ai = NonNullable<Me["computer"]>["ai"];

/** Where the connect panel was opened: a provider's row, or Add a provider's window. */
type Opened = Open & { where: "row" | "window" };

/** The connect panel's Cancel: not while a connect runs, and not for one this page didn't open. */
function SlotCancel({ ai, open, onDone }: { ai: Ai; open: Opened | null; onDone: () => void }) {
  return (
    <Show when={open && ai.status !== "connecting"}>
      <Button variant="ghost" className="self-start" onClick={onDone}>
        Cancel
      </Button>
    </Show>
  );
}

/** The connect panel, where it was opened: in a row it's titled and boxed; in the window it's the step. */
function ConnectSlot({
  ai,
  open,
  connect,
  onDone,
}: {
  ai: Ai;
  open: Opened | null;
  connect: Connect;
  onDone: () => void;
}) {
  const boxed = open?.where !== "window";
  return (
    <div
      className={cn("flex flex-col gap-4", boxed && "rounded-xl bg-muted p-4 sm:p-5")}
      data-connect-slot
    >
      <Show when={open && boxed}>
        <p className="text-body font-medium text-foreground">{open?.title}</p>
      </Show>
      <ConnectPanel
        ai={ai}
        provider={open?.provider}
        account={open?.account}
        connect={connect}
        askModel={connectPicksDefault(ai)}
        onConnected={onDone}
      />
      <SlotCancel ai={ai} open={open} onDone={onDone} />
    </div>
  );
}

/** A thread's Reconnect lands on `/settings#ai-default`: scroll to it once the rows are there. */
function useScrollToHash(ready: boolean) {
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (ready && id) document.getElementById(id)?.scrollIntoView();
  }, [ready]);
}

/** Why asks are affected, above the cards. */
function AiNotice({ ai }: { ai: Ai }) {
  const models = useModels();
  const notice = aiNotice(ai, models.data);
  return (
    <Show when={notice}>
      <p
        role="status"
        data-ai-notice
        className="flex items-start gap-2 rounded-lg bg-destructive/10 px-3 py-2 text-sm text-brick"
      >
        <StatusDot tone="error" className="mt-1.5" />
        {notice}
      </p>
    </Show>
  );
}

/**
 * The connect panel's state: what it's open for and where, the connect it
 * started (followed wherever the panel shows), and Add a provider's window.
 * Closing the window leaves a connect it started to that provider's row.
 */
function useConnectSlot(ai: Ai) {
  const connect = useConnectAi();
  const { reset } = connect;
  const [open, setOpen] = useState<Opened | null>(null);
  const [adding, setAdding] = useState(false);
  // The window is modal: while it shows, the panel is its own or closed.
  const close = useCallback(() => {
    setOpen(null);
    reset();
  }, [reset]);
  const done = useCallback(() => {
    setAdding(false);
    close();
  }, [close]);
  const opening = (next: Opened) => {
    reset();
    setOpen(next);
  };
  // A key the window connected goes on in its row; kept in render, as `/api/me` moves.
  if (open && handsOff(open, connect.isSuccess, ai)) {
    setOpen({ ...open, where: "row" });
    setAdding(false);
  }
  const slot = <ConnectSlot ai={ai} open={open} connect={connect} onDone={done} />;
  const places = slotPlaces(open, ai);
  return {
    slotFor: (provider: AiProvider) => (places.row === provider ? slot : null),
    orphan: places.orphan ? slot : null,
    openRow: (next: Open) => opening({ ...next, where: "row" }),
    window: {
      show: adding,
      picked: places.window,
      slot: places.window ? slot : null,
      onWay: (provider: AiProvider) =>
        opening({ provider, title: `Connect ${PROVIDERS[provider].name}`, where: "window" }),
      onBack: close,
      // A connect under way goes on under Providers, its provider kept (a
      // Grok code keeps xAI's words); otherwise the window just closes.
      onClose: () => {
        if (open?.where === "window" && (connect.isPending || connect.isSuccess)) {
          setOpen({ ...open, where: "row" });
          setAdding(false);
        } else done();
      },
    },
    add: () => {
      close();
      setAdding(true);
    },
  };
}

/** Settings → AI for the owner: Models, Providers, and Add a provider. */
export function AiSettings({ ai, timezone }: { ai: Ai; timezone: string }) {
  const panel = useConnectSlot(ai);
  const { mutate: readHealth } = useReadHealth();
  const now = useNow();
  useEffect(() => readHealth(), [readHealth]);
  const busy = ai.status === "connecting";
  const cards = providerCards(ai);
  useScrollToHash(cards.length > 0);
  const models = useModels();
  const draft = useRoutingDraft(ai, models.data);
  return (
    // On a phone the bar is fixed over the page's end: room for it under the last card.
    <div className={cn("flex flex-col gap-6", draft.view.kind !== "none" && "pb-44 md:pb-0")}>
      <AiNotice ai={ai} />
      <Show when={hasCredential(ai)}>
        <ModelsCard ai={ai} busy={busy} draft={draft} />
      </Show>
      <ProvidersCard
        ai={ai}
        cards={cards}
        now={now}
        timezone={timezone}
        busy={busy}
        anchor={defaultCard(ai)}
        slotFor={panel.slotFor}
        orphan={panel.orphan}
        canAdd={makerRows(ai).some((r) => r.open)}
        onAdd={panel.add}
        onOpen={panel.openRow}
      />
      <AddProviderWindow ai={ai} busy={busy} {...panel.window} />
      <SaveBar draft={draft} />
      <LeavePrompt draft={draft} />
    </div>
  );
}
